import { API, graphqlOperation } from 'aws-amplify'
import { useEffect, useRef, useState } from 'react'

const op = `query ListOccupants($d1: String, $sk: String, $sk2: String, $nextToken: String, $limit: Int) {
    listOccupants(d1: $d1, sk: $sk, sk2: $sk2, nextToken: $nextToken, limit: $limit) {
          items {
            pk
            sk
            d1
            d2
            email
            firstName
            lastLogin
            registrationDate
            lastName
            primaryContact
            phone {
                number
                extension
            }
            roles
            workPhone {
                number
                extension
            }
            extraFields {
                key
                value
            }
          }
          nextToken
        }
      }
`

const cache = {}
const CACHE_VERSION = 4 // Increment to invalidate old cache
const MAX_PAGES = 50    // Safety limit on pagination

// Global cache invalidation support - can be called from anywhere to force refresh.
// The timestamp is read by in-flight fetches so they don't write a stale result
// back into the cache after it was cleared out from under them.
let cacheInvalidationTimestamp = 0

export function invalidateOccupantsCache() {
    cacheInvalidationTimestamp = Date.now()
    Object.keys(cache).forEach(key => delete cache[key])
}

// Module-level listener ensures the cache is cleared even when no hook instance is
// mounted (e.g. user navigated away from the Users page before creating an association).
// This is the ONLY place that clears the cache on this event - per-instance listeners
// just trigger their own refetch, otherwise N mounted hooks wipe the whole cache N times.
if (typeof window !== 'undefined') {
    window.addEventListener('occupant-data-updated', () => {
        invalidateOccupantsCache()
    })
}

function buildCacheKey(sk, d1, sk2) {
    return sk2 ? `${sk2}-${d1}` : sk
}

/**
 * @param {string|null} sk        Sort key, e.g. `OCCUPANCY#${tenantPk}`. Used when sk2 is absent.
 * @param {string|null} d1        Building id. Required when sk2 is used.
 * @param {string|null} nextToken Unused - pagination always starts from the first page.
 * @param {number}      limit     Page size.
 * @param {string|null} sk2       Secondary sort key, e.g. "OCCUPANCY".
 * @param {number}      refreshKey Bump this number to force a cache-bypassing refetch.
 *
 * @returns {{ error, loading, data, dataKey }} `dataKey` identifies which sk/d1
 *          combination `data` actually belongs to, so consumers can ignore a render
 *          where the props have moved on but the data hasn't caught up yet.
 */
function useListOccupants(sk, d1, nextToken, limit, sk2, refreshKey = 0) {
    const [error, setErrors] = useState(null)
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState([])
    const [dataKey, setDataKey] = useState(null)
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    // Tracks the last refreshKey we acted on, so a change in d1 alone doesn't
    // needlessly bypass the cache.
    const lastRefreshKey = useRef(refreshKey)

    // Listen for occupant data updates (archive/restore/create/delete).
    // Cache clearing is handled by the module-level listener above.
    useEffect(() => {
        const handleOccupantUpdate = () => {
            setRefreshTrigger(prev => prev + 1)
        }

        window.addEventListener('occupant-data-updated', handleOccupantUpdate)

        return () => {
            window.removeEventListener('occupant-data-updated', handleOccupantUpdate)
        }
    }, [])

    useEffect(() => {
        // Set by the cleanup function when sk/d1/sk2 change or the hook unmounts.
        // Every await below checks it, so a superseded fetch stops paginating and
        // never calls setState - this is what prevents a slow request for building A
        // from overwriting a fast one for building B.
        let ignore = false

        const cacheKey = buildCacheKey(sk, d1, sk2)

        const bypassCache = refreshKey !== lastRefreshKey.current
        lastRefreshKey.current = refreshKey

        function applyResult(items) {
            setData(items)
            setDataKey(cacheKey)
            setLoading(false)
        }

        async function fetchOccupants() {
            let cached = cache[cacheKey]

            // Check cache version - invalidate if old version
            if (cached && cached.version !== CACHE_VERSION) {
                cached = null
                delete cache[cacheKey]
            }

            if (bypassCache) {
                cached = null
                delete cache[cacheKey]
            }

            if (cached) {
                applyResult(cached.items)
                return
            }

            setLoading(true)

            // Recorded before the first request so we can tell whether the cache was
            // invalidated while we were paginating.
            const startedAt = Date.now()

            try {
                const allItems = []
                const seenUserTenantPairs = new Set() // Track unique user+tenant combinations
                let currentNextToken = null           // Start from first page
                let pageCount = 0

                do {
                    pageCount++

                    if (pageCount > MAX_PAGES) {
                        console.warn(
                            `[useListOccupants] Reached max ${MAX_PAGES} pages, stopping. Total: ${allItems.length}`
                        )
                        break
                    }

                    const params = {
                        d1: d1,
                        sk: sk,
                        sk2: sk2,
                        nextToken: currentNextToken,
                        limit: limit,
                    }

                    const response = await API.graphql(graphqlOperation(op, params))

                    // Props changed while this page was in flight - abandon the whole
                    // loop rather than burning up to 49 more requests for stale data.
                    if (ignore) return

                    const result = response.data.listOccupants

                    if (!result.items || result.items.length === 0) {
                        break // No more items
                    }

                    for (const item of result.items) {
                        // Extract tenant ID from sk field (format: OCCUPANCY#tenantId)
                        const tenantId = item.sk?.split('#')[1]

                        // Unique key from user pk + tenant ID. Allows the same user to
                        // appear in multiple tenants, but only once per tenant.
                        const uniqueKey = `${item.pk}|${tenantId}`

                        if (item.pk && !seenUserTenantPairs.has(uniqueKey)) {
                            allItems.push(item)
                            seenUserTenantPairs.add(uniqueKey)
                        }
                    }

                    currentNextToken = result.nextToken
                } while (currentNextToken)

                if (ignore) return

                // Only cache if nothing invalidated it while we were fetching,
                // otherwise we'd resurrect data that was just marked stale.
                if (cacheInvalidationTimestamp < startedAt) {
                    cache[cacheKey] = { items: allItems, nextToken: null, version: CACHE_VERSION }
                }

                applyResult(allItems)
            } catch (response) {
                if (ignore) return
                console.error(response?.errors ?? response)
                setErrors(response?.errors ?? response)
                setLoading(false)
            }
        }

        // Required params not ready yet (e.g. d1 hasn't resolved during a building
        // switch). Clear the previous building's data and stay in loading, so the
        // consumer never sees stale rows paired with loading === false.
        const missingParams = sk2 ? !d1 : !sk

        if (missingParams) {
            setData([])
            setDataKey(null)
            setLoading(true)
            return
        }

        fetchOccupants()

        return () => {
            ignore = true
        }
    }, [sk, d1, sk2, limit, refreshKey, refreshTrigger])

    return { error, loading, data, dataKey }
}

export default useListOccupants